/**
 * Value equality as a USER sees it in two inputs, rather than as `===` sees it: every empty form is one
 * emptiness, a number equals its string form, and Dates/arrays/objects compare by content. Built for the
 * side-by-side wizard's copy column (is this row's value already the same on both sides?), but the question
 * is general — anything comparing two renderings of the same field can use it.
 *
 * Each branch is also exposed on its own, for a caller that already knows the kind of value it holds. Every
 * `areXEqual` is self-guarding: it answers `false` when only one side is of its kind, since a value that
 * cannot be judged is safer reported as different than as identical.
 */
export default class compareHelper {
  static areValuesEqual(first: unknown, second: unknown): boolean {
    if (compareHelper.isEitherBlank(first, second)) {
      return compareHelper.areBothBlank(first, second);
    }
    if (compareHelper.isEitherDate(first, second)) {
      return compareHelper.areDatesEqual(first, second);
    }
    if (compareHelper.isEitherArray(first, second)) {
      return compareHelper.areArraysEqual(first, second);
    }
    if (compareHelper.isEitherObject(first, second)) {
      return compareHelper.areObjectsEqual(first, second);
    }
    return compareHelper.areTextsEqual(first, second);
  }

  //#region Blank
  // Blank = what an input shows as empty. `false` and `0` are NOT blank - they are values a user picked.
  static isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  }

  static isEitherBlank(first: unknown, second: unknown): boolean {
    return compareHelper.isBlank(first) || compareHelper.isBlank(second);
  }

  static areBothBlank(first: unknown, second: unknown): boolean {
    return compareHelper.isBlank(first) && compareHelper.isBlank(second);
  }
  //#endregion

  //#region Dates
  static isEitherDate(first: unknown, second: unknown): boolean {
    return first instanceof Date || second instanceof Date;
  }

  /** By instant, so two Dates built from the same value in different ways still match. */
  static areDatesEqual(first: unknown, second: unknown): boolean {
    if (!(first instanceof Date) || !(second instanceof Date)) {
      return false;
    }
    return first.getTime() === second.getTime();
  }
  //#endregion

  //#region Arrays
  static isEitherArray(first: unknown, second: unknown): boolean {
    return Array.isArray(first) || Array.isArray(second);
  }

  /** Element-wise and in order, each element judged by `areValuesEqual` (a multi-select holds ids). */
  static areArraysEqual(first: unknown, second: unknown): boolean {
    if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) {
      return false;
    }
    return first.every((value, index) => compareHelper.areValuesEqual(value, second[index]));
  }
  //#endregion

  //#region Objects
  // Arrays are objects too, which is why areValuesEqual asks about them first.
  static isObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null;
  }

  static isEitherObject(first: unknown, second: unknown): boolean {
    return compareHelper.isObject(first) || compareHelper.isObject(second);
  }

  /** Key-wise, each value judged by `areValuesEqual` (a date range is a small object). */
  static areObjectsEqual(first: unknown, second: unknown): boolean {
    if (!compareHelper.isObject(first) || !compareHelper.isObject(second)) {
      return false;
    }
    const firstObject = first as Record<string, unknown>;
    const secondObject = second as Record<string, unknown>;
    const keys = Object.keys(firstObject);
    if (keys.length !== Object.keys(secondObject).length) {
      return false;
    }
    return keys.every((key) => compareHelper.areValuesEqual(firstObject[key], secondObject[key]));
  }
  //#endregion

  // A number and its string form are the same value: an edited numeric input hands back a string while the
  // item built from the API holds a number, and the two inputs still read identically.
  static areTextsEqual(first: unknown, second: unknown): boolean {
    return String(first) === String(second);
  }
}
